import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Head from './layouts/Head';
import LoadingDashboardContent from '../components/LoadingDashboardContent';
import { PageList } from '../components/PageComponents';
import { API_URL, APP_URL } from '../constants';

const AdminNotifications = ({ userRole, userToken }) => {
  const { search } = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState({ data: [], dataCount: 0 });
  const [isReload, setIsReload] = useState(false);

  // Query params
  const params = new URLSearchParams(search);
  const currentPage = Number(params.get('page')) || 1;
  const limit = Number(params.get('limit')) || 20;
  const searchBy = params.get('searchBy') || 'title';
  const searchValue = params.get('searchValue') || '';

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/notification`, {
        params: { page: currentPage, limit, searchBy, searchValue },
        headers: { Authorization: `Bearer ${userToken}` },
      });
      setPageData({
        data: res.data?.data || [],
        dataCount: res.data?.dataCount || 0,
      });
    } catch (err) {
      toast.error('Failed to fetch notifications.');
    } finally {
      setLoading(false);
      setIsReload(false);
    }
  }, [currentPage, limit, searchBy, searchValue, userToken]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications, isReload]);

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Generate link for notification
  const getNotificationLink = (item) => {
    let meta = {};
    try {
      meta = typeof item.meta === 'object' ? item.meta : JSON.parse(item.meta || '{}');
    } catch {}
    switch (item.type) {
      case 'order':
        if (meta.orderId) {
          // Vendor order update page
          return `${APP_URL}${userRole}/order/update/${meta.orderId}`;
        }
        break;
      case 'product':
        if (meta.productId) {
          // Vendor product edit page
          return `${APP_URL}${userRole}/product/edit/${meta.productId}`;
        }
        break;
      case 'admin_alert':
        if (meta.vendorId && item.title && item.title.includes('New Vendor Registration')) {
          return `${APP_URL}${userRole}/vendor/edit/${meta.vendorId}`;
        } else if (meta.customerId && item.title && item.title.includes('New Customer Registered')) {
          return `${APP_URL}${userRole}/customer/edit/${meta.customerId}`;
        } else if (meta.productId && item.title && item.title.includes('New Product Pending Approval')) {
          return `${APP_URL}${userRole}/product/edit/${meta.productId}`;
        } else if (meta.ticketId && item.title && item.title.includes('New Support Ticket Received')) {
          return `${APP_URL}${userRole}/support/edit/${meta.ticketId}`;
        } else if (meta.payoutId && item.title && item.title.includes('New Payout Request')) {
          return `${APP_URL}${userRole}/payout/edit/${meta.payoutId}`;
        }
        break;
      case 'payout':
        if (meta.payoutId) {
          // Vendor payout view page
          return `${APP_URL}${userRole}/payout/view/${meta.payoutId}`;
        }
        break;
      case 'support':
        if (meta.ticketId) {
          // Vendor support ticket edit page
          return `${APP_URL}${userRole}/support/edit/${meta.ticketId}`;
        }
        break;
      // Add more cases as needed for other notification types
      // case 'account':
      //   return `${APP_URL}${userRole}profile`;
      default:
        break;
    }
    // Fallback to notifications list
    return `${APP_URL}/${userRole}/notifications/list`;
  };

  // Mark as read and navigate
  const handleView = async (item) => {
    if (item.is_read === '0') {
      try {
        await axios.post(`${API_URL}/notification/read/${item.id}`, {}, {
          headers: { Authorization: `Bearer ${userToken}` }
        });
        setPageData((prev) => ({
          ...prev,
          data: prev.data.map(n => n.id === item.id ? { ...n, is_read: '1' } : n)
        }));
      } catch {
        toast.error('Failed to mark as read.');
      }
    }
    window.location.href = getNotificationLink(item);
  };

  // Delete notification
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification?')) return;
    try {
      await axios.delete(`${API_URL}/notification/${id}`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });
      toast.success('Deleted!');
      setIsReload(true);
    } catch {
      toast.error('Delete failed');
    }
  };

  // Update URL params for pagination/search
  const updateUrlParams = (page, limitVal, searchByVal, searchValueVal) => {
    const params = new URLSearchParams();
    if (page) params.set('page', page);
    if (limitVal) params.set('limit', limitVal);
    if (searchByVal) params.set('searchBy', searchByVal);
    if (searchValueVal) params.set('searchValue', searchValueVal);
    navigate({ pathname: `/${userRole}/notifications/list`, search: params.toString() });
    setIsReload(true);
  };

  return (
    <>
      <Head title="Notifications" />
      {loading ? (
        <LoadingDashboardContent />
      ) : (
        <PageList
          userToken={userToken}
          itemType="notification"
          itemId="id"
          pageTitle="Notifications"
          pageData={pageData.data}
          totalRows={pageData.dataCount}
          currentPage={currentPage}
          limit={limit}
          searchBy={searchBy}
          searchValue={searchValue}
          actionApplyKeyMap={{ Delete: 'delete' }}
          columns={['Title', 'Message', 'Type', 'Status', 'Date', 'Actions']}
          filterBy={['title', 'content', 'type']}
          columnKeyMap={{
            Title: (item) => (<>{item.title}<span className='a_view ms-2' onClick={() => handleView(item)}><i className='bi bi-eye'></i></span></>),
            Message: item => (
              <div
                dangerouslySetInnerHTML={{ __html: item.content }}
                className={item.is_read === '0' ? 'fw-bold text-dark' : 'text-muted'}
              />
            ),
            Type: item => (
              <span className="badge bg-secondary">
                {item.type ? item.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : 'N/A'}
              </span>
            ),
            Status: item => (
              <span className={`badge ${item.is_read === '0' ? 'bg-warning text-dark' : 'bg-success'}`}>
                {item.is_read === '0' ? 'Unread' : 'Read'}
              </span>
            ),
            Date: item => <small className="text-muted">{formatDate(item.createdAt)}</small>,
          }}
          onDelete={handleDelete}
          onAddNew={false}
          updateUrlParams={updateUrlParams}
          isReload={isReload}
          setIsReload={setIsReload}
        />
      )}
    </>
  );
};

export default AdminNotifications;